/* ============================================================
 * 商品ビルダー
 * カテゴリー → サイズ → 色 → 個数 → ギフト → 確認 の順に、
 * 全画面モーダルの中を1画面ずつスワイプで進めていくUIです。
 * ============================================================ */

class ProductBuilder {
  /* パンくずに使う小さなアイコンです（商品＝かばん、サイズ＝ものさし、色＝しずく）。 */
  static ICONS = {
    category:
      '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 8h14l-1 12H6L5 8Z"/><path d="M9 8V6a3 3 0 0 1 6 0v2"/></svg>',
    size:
      '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="9" width="18" height="6" rx="1"/><path d="M8 9v3M12 9v3M16 9v3"/></svg>',
    color:
      '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3s6 6.4 6 9.8A6 6 0 0 1 6 12.8C6 9.4 12 3 12 3Z"/></svg>',
  };

  static STORAGE_VERSION = 1;
  static STORAGE_MAX_AGE = 7 * 24 * 60 * 60 * 1000;

  constructor(root) {
    this.root = root;
    this.config = ProductBuilder.readConfig(root);
    this.isModal = root.dataset.displayMode === 'modal';

    this.modal = root.querySelector('[data-modal]');
    this.stage = root.querySelector('[data-stage]');
    this.track = root.querySelector('[data-track]');
    this.panels = Array.from(root.querySelectorAll('[data-panel]'));
    this.continueBtn = root.querySelector('[data-continue]');
    this.shipLeadEl = root.querySelector('[data-ship-lead]');
    this.shipInfoBtn = root.querySelector('[data-ship-info]');
    this.shipTipEl = root.querySelector('[data-ship-tip]');
    this.shipNotesEl = root.querySelector('[data-ship-notes]');
    this.prevBtn = root.querySelector('[data-prev]');
    this.nextBtn = root.querySelector('[data-next]');
    this.errorEl = root.querySelector('[data-error]');
    this.recapEl = root.querySelector('[data-recap]');
    this.confirmEl = root.querySelector('[data-confirm]');
    // パソコンでは段を縦に積みます。スマホは1画面ずつのままです。
    this.wide = window.matchMedia('(min-width: 750px)');
    this.closeBtn = root.querySelector('[data-pb-close]');
    this.checkoutBtn = root.querySelector('[data-checkout]');
    this.doneItemEl = root.querySelector('[data-done-item]');
    this.cartCountEl = root.querySelector('[data-cart-count]');
    this.recoHeadingEl = root.querySelector('[data-reco-heading]');
    this.recoSecEl = root.querySelector('[data-reco-sec]');


    // 選択状態です。ギフトは「ギフトにしない」を初期値にしておき、
    // 後半のステップが心理的な障壁にならないようにしています。
    this.state = {
      category: null,
      size: null,
      // 色ごとの数量です。{ バリアントID: 個数 } の形で持ちます。
      quantities: {},
    };

    this.steps = [];
    this.stepIndex = 0;
    this.lastFocused = null;
    this.completed = false;
    this.showDetail = false;
    this.detailSource = null;
    this.cartCount = 0;
    this.cartTotal = 0;
    this.restoring = false;

    // 必要な要素が揃っていないときは、例外で他の処理を巻き込まないよう中断します。
    if (!this.stage || !this.track || this.panels.length === 0) {
      console.warn('[product-builder] 必要な要素が見つからないため初期化を中止しました');
      return;
    }

    this.bindEvents();
    this.bindModal();
    this.bindHashTriggers();
    this.autoSelectSingleCategory();
    const deepLinked = this.restoreFromUrl();
    this.refresh();

    // 広告などから色まで指定して来られた場合は、答え済みの画面を飛ばして先に進みます。
    // そうでなければ、前回の続きがあればそこから再開します。
    if (deepLinked) {
      this.goTo(this.firstUnansweredIndex(), { animate: false });
    } else if (!this.restoreState()) {
      this.goTo(0, { animate: false });
    }

    if (deepLinked && this.isModal) this.open();
  }

  static readConfig(root) {
    const el = root.querySelector('[data-pb-config]');
    if (!el) return {};
    try {
      return JSON.parse(el.textContent);
    } catch (error) {
      console.error('[product-builder] 設定の読み込みに失敗しました', error);
      return {};
    }
  }

  /* ---------- 初期化 ---------- */

  bindEvents() {
    this.root.addEventListener('click', (event) => {
      const choice = event.target.closest('[data-choice]');
      if (choice && this.root.contains(choice)) {
        this.onChoice(choice);
        return;
      }

      const itemStep = event.target.closest('[data-item-step]');
      if (itemStep) {
        const card = itemStep.closest('[data-item]');
        if (card) this.changeQuantity(card, Number(itemStep.dataset.itemStep));
        return;
      }

      const reco = event.target.closest('.pb-reco__card');
      if (reco && !reco.disabled) {
        this.onRecommendation(reco);
        return;
      }

      if (event.target.closest('[data-confirm-ok]')) {
        const run = this.pendingConfirm;
        this.closeConfirm();
        if (run) run();
        return;
      }
      if (event.target.closest('[data-confirm-cancel]') || event.target === this.confirmEl) {
        this.closeConfirm();
        return;
      }

      const infoBtn = event.target.closest('[data-ship-info]');
      if (infoBtn) {
        // 指で触る端末では重ねられないので、押して開け閉めします。
        const wrap = infoBtn.closest('[data-ship-tip]');
        const open = infoBtn.getAttribute('aria-expanded') === 'true';
        infoBtn.setAttribute('aria-expanded', String(!open));
        if (wrap) wrap.classList.toggle('is-open', !open);
        return;
      }

      // 吹き出しの外を押したら閉じます。
      if (this.shipTipEl && this.shipTipEl.classList.contains('is-open')) {
        this.shipTipEl.classList.remove('is-open');
        if (this.shipInfoBtn) this.shipInfoBtn.setAttribute('aria-expanded', 'false');
      }

      if (event.target.closest('[data-view-cart]')) {
        this.leaveTo(this.config.cartUrl);
        return;
      }

      if (event.target.closest('[data-continue]')) {
        this.restart();
        return;
      }

      if (event.target.closest('[data-checkout]')) {
        this.leaveTo(this.checkoutUrl());
        return;
      }

      if (event.target.closest('[data-prev]')) this.movePrev();
      if (event.target.closest('[data-next]')) this.onNext();
    });

    // 数字を直接入れられる窓です。入力のたびに範囲へ収めます。
    this.root.addEventListener('change', (event) => {
      const input = event.target.closest('[data-item-input]');
      if (!input) return;
      const card = input.closest('[data-item]');
      if (!card) return;
      const value = parseInt(input.value, 10);
      this.setQuantity(card, Number.isFinite(value) ? value : 0);
    });

    // 画面幅が変わったら、縦積みと横スライドを切り替えます。
    if (this.wide && typeof this.wide.addEventListener === 'function') {
      this.wide.addEventListener('change', () => this.goTo(this.stepIndex, { animate: false }));
    }

    /*
     * テーマエディタでの下見用です。
     * 実際に操作しなくても、開いた状態や完了画面を確認できるようにします。
     */
    if (window.Shopify && window.Shopify.designMode) {
      if (this.config.editorDone) {
        this.fillPreviewChoices();
        this.completed = true;
        this.cartCount = this.cartCount || 1;
      }
      if (this.config.editorOpen || this.config.editorDone) {
        window.requestAnimationFrame(() => this.open());
      }
    }

    this.bindSwipe();

    // ページ内に直接表示するモードでは、パネルの高さに合わせて枠を伸縮させます。
    if (!this.isModal) {
      if (typeof ResizeObserver !== 'undefined') {
        this.observer = new ResizeObserver(() => this.syncHeight());
        this.panels.forEach((panel) => this.observer.observe(panel));
      }
      window.addEventListener('resize', () => this.syncHeight());
    }
  }

  /* 横方向のスワイプで前後のステップに移動できるようにします。 */
  bindSwipe() {
    let startX = 0;
    let startY = 0;
    let delta = 0;
    let dragging = false;
    let horizontal = null;

    const onDown = (event) => {
      // 縦に積んでいるときは、横スワイプで段を移動させません。
      if (this.isStacked()) return;
      if (event.pointerType === 'mouse') return;
      if (event.target.closest('button, a, input, select, textarea')) return;
      startX = event.clientX;
      startY = event.clientY;
      delta = 0;
      dragging = true;
      horizontal = null;
    };

    const onMove = (event) => {
      if (!dragging) return;
      const dx = event.clientX - startX;
      const dy = event.clientY - startY;

      if (horizontal === null) {
        if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
        horizontal = Math.abs(dx) > Math.abs(dy);
        if (horizontal) this.track.classList.add('is-dragging');
      }
      if (!horizontal) return;

      // 進めない方向へのスワイプは、手応えを残しつつ動きを抑えます。
      const blocked = (dx < 0 && !this.canAdvance()) || (dx > 0 && this.stepIndex === 0);
      delta = blocked ? dx * 0.25 : dx;
      const base = -this.panelIndexOf(this.currentStep()) * 100;
      this.track.style.transform = `translateX(calc(${base}% + ${delta}px))`;
    };

    const onUp = () => {
      if (!dragging) return;
      dragging = false;
      this.track.classList.remove('is-dragging');

      // 横に動かす意図がなかったとき（タップや縦スクロール）は何もしません。
      // ここで goTo を呼ぶとパネルのスクロール位置が先頭へ戻り、
      // ボタンを押そうとしただけで画面が飛んでしまいます。
      if (!horizontal) {
        delta = 0;
        horizontal = null;
        return;
      }

      if (delta <= -60) this.moveNext();
      else if (delta >= 60) this.movePrev();
      else this.snapBack();

      delta = 0;
      horizontal = null;
    };

    this.stage.addEventListener('pointerdown', onDown);
    this.stage.addEventListener('pointermove', onMove);
    this.stage.addEventListener('pointerup', onUp);
    this.stage.addEventListener('pointercancel', onUp);
  }

  /* 途中まで引いたスワイプを、元の位置へ戻すだけの処理です（再描画やスクロールはしません）。 */
  snapBack() {
    this.track.style.transform = `translateX(-${this.panelIndexOf(this.currentStep()) * 100}%)`;
  }

  /*
   * チェックアウトのURLです。
   * routes には checkout の項目がないため、ストアのルート（多言語なら /en/ など）に
   * checkout を足して組み立てます。Shopify.routes.root は必ず / で終わります。
   */
  checkoutUrl() {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return `${root}checkout`;
  }

  /* モーダルを閉じてから移動します。スクロールが固まったままにならないようにするためです。 */
  leaveTo(url) {
    if (!url) return;
    document.body.classList.remove('pb-lock');
    window.location.href = url;
  }

  /*
   * リンクやURLから「#◯◯」の部分だけを取り出します。
   * 別ページへのリンク（/pages/xxx#◯◯）でも拾えるようにするためです。
   */
  hashOf(value) {
    if (!value) return '';
    const hash = String(value).split('#')[1];
    if (!hash) return '';
    try {
      return decodeURIComponent(hash);
    } catch (error) {
      return hash;
    }
  }

  /*
   * 下見用に、いちばん上の商品を選んだことにします。
   * 何も選んでいないと「をカートに追加しました。」と主語が抜けてしまい、
   * 仕上がりを確かめられないためです。
   */
  fillPreviewChoices() {
    const category = this.root.querySelector('[data-choice="category"]:not([disabled])');
    if (category) this.selectCategory(category);

    const sizeGroup = this.sizeGroup();
    const size = sizeGroup
      ? sizeGroup.querySelector('[data-choice="size"]:not([disabled])')
      : null;
    if (size) this.selectSize(size);

    const colorGroup = this.colorGroup();
    const color = colorGroup ? colorGroup.querySelector('[data-item]:not([data-soldout])') : null;
    if (color) this.state.quantities[color.dataset.value] = 1;
  }

  /* ---------- モーダルの開閉 ---------- */

  /* URLのハッシュと、あとから差し込まれたリンクからも開けるようにします。
     受け付けるハッシュは次の2つです。
       #product-builder-button-floating … モーダル本体のID（既存リンクとの互換用）
       #product-builder                 … どの要素にも一致しない別名。
                                          一致する要素が無いのでブラウザが勝手にスクロールしません。
     PageFly など、初期化より後にリンクを差し込む仕組みにも効くよう、
     クリックは document 側で受け取ります（イベント委譲）。 */
  bindHashTriggers() {
    if (!this.isModal) return;

    // 同じページに複数のビルダーがある場合、別名で開くのは最初の1つだけにします。
    if (!window.__pbAliasOwner) window.__pbAliasOwner = this;
    const aliases = window.__pbAliasOwner === this
      ? [this.root.id, 'product-builder']
      : [this.root.id];

    const matches = (hash) => {
      if (!hash) return false;
      return aliases.indexOf(hash.replace(/^#/, '')) !== -1;
    };

    document.addEventListener('click', (event) => {
      if (event.defaultPrevented) return;
      const target = event.target;
      if (!target || typeof target.closest !== 'function') return;
      const link = target.closest('a[href*="#"]');
      if (!link) return;

      let url;
      try {
        url = new URL(link.href, window.location.href);
      } catch (error) {
        return;
      }
      if (!matches(url.hash)) return;
      // 別ページへのリンクはそのまま遷移させ、遷移先でハッシュから開かせます。
      if (url.pathname !== window.location.pathname) return;

      event.preventDefault();
      this.open();
    });

    const openFromHash = () => {
      if (!matches(window.location.hash)) return;
      if (this.isOpen()) return;
      this.open();
    };

    window.addEventListener('hashchange', openFromHash);
    openFromHash();
  }

  bindModal() {
    if (!this.isModal) return;

    this.root.querySelectorAll('[data-pb-open]').forEach((btn) => {
      btn.addEventListener('click', () => this.open());
    });

    /*
     * ページ内の任意のリンク（#モーダルID）からも開けるようにします。
     * リンクは後から差し込まれることもあるため、個々に付けず
     * 画面全体で受け取ります。
     */
    document.addEventListener('click', (event) => {
      const link = event.target.closest('a[href]');
      if (!link) return;
      if (this.hashOf(link.getAttribute('href')) !== this.root.id) return;
      event.preventDefault();
      this.open();
    });

    /*
     * URL に #モーダルID を付けて開いたときも、そのまま開きます。
     *
     * ブラウザは同じ名前の要素まで自動で送ってしまいます。
     * モーダルはこの場所と関係なく画面全体を覆うので、
     * 閉じたときに見当違いの位置にいないよう、先頭へ戻しておきます。
     */
    const openFromHash = () => {
      if (this.hashOf(window.location.hash) !== this.root.id) return;
      window.scrollTo(0, 0);
      this.open();
    };
    window.addEventListener('hashchange', openFromHash);
    openFromHash();

    if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.close());

    document.addEventListener('keydown', (event) => {
      if (!this.isOpen()) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
      }
      if (event.key === 'Tab') this.trapFocus(event);
    });
  }

  isOpen() {
    return Boolean(this.modal) && this.modal.classList.contains('is-open');
  }

  open() {
    if (!this.isModal || this.isOpen()) return;
    this.lastFocused = document.activeElement;
    this.modal.classList.add('is-open');
    document.body.classList.add('pb-lock');
    this.pushDataLayer('pb_modal_open');
    // 完了画面のまま閉じていた場合は、最初の質問から選び直せるようにします。
    // ただしテーマエディタでの下見中は、完了画面を見せたままにします。
    const previewing = Boolean(
      window.Shopify && window.Shopify.designMode && this.config.editorDone
    );
    if (this.completed && !previewing) this.restart();
    // 送料無料の判定に使うため、いまのカート金額を取り直します。
    this.fetchCartCount().then((count) => {
      this.cartCount = count;
      this.refresh();
    });
    this.refresh();
    // 表示位置は transform で保持しているため、開いた直後に現在地を描き直します。
    this.goTo(this.stepIndex, { animate: false });
    if (this.closeBtn) this.closeBtn.focus();
  }

  close() {
    if (!this.isOpen()) return;
    this.modal.classList.remove('is-open');
    document.body.classList.remove('pb-lock');
    if (this.lastFocused && typeof this.lastFocused.focus === 'function') this.lastFocused.focus();
  }

  /* モーダルの外にフォーカスが出ていかないようにします。 */
  trapFocus(event) {
    const focusable = Array.from(
      this.modal.querySelectorAll('button:not([disabled]), [href], input, select, [tabindex]:not([tabindex="-1"])')
    ).filter((el) => el.offsetParent !== null);
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  autoSelectSingleCategory() {
    const categories = this.root.querySelectorAll('[data-choice="category"]');
    if (categories.length === 1) this.selectCategory(categories[0]);
  }

  /* 広告などから ?variant=xxxx で特定の色に直接着地できるようにします。 */
  restoreFromUrl() {
    const variantId = new URLSearchParams(window.location.search).get('variant');
    if (!variantId) return false;

    const colorCard = this.root.querySelector(
      `[data-item][data-value="${CSS.escape(variantId)}"]:not([data-soldout])`
    );
    if (!colorCard) return false;

    const group = colorCard.closest('[data-color-group]');
    const sizeCard = group ? this.card('size', group.dataset.colorGroup) : null;
    if (!sizeCard) return false;

    this.selectSizeWithCategory(sizeCard);
    // 広告から来た色を1つ選んだ状態にします。
    this.state.quantities[variantId] = 1;
    return true;
  }

  /* サイズを選ぶとき、そのサイズが属するカテゴリーも併せて選択状態にします。 */
  selectSizeWithCategory(sizeCard) {
    const sizeGroup = sizeCard.closest('[data-size-group]');
    const categoryCard = sizeGroup ? this.card('category', sizeGroup.dataset.sizeGroup) : null;
    if (categoryCard) this.selectCategory(categoryCard);
    this.selectSize(sizeCard);
  }

  /* ---------- 途中経過の保存と復元 ---------- */

  storageKey() {
    return `pb:${this.root.id || 'product-builder'}`;
  }

  saveState() {
    // 復元処理の最中は書き戻しません（復元前の値で上書きしてしまうため）。
    if (this.restoring) return;

    const value = (card) => (card ? card.dataset.value : null);
    const data = {
      v: ProductBuilder.STORAGE_VERSION,
      t: Date.now(),
      category: value(this.state.category),
      size: value(this.state.size),
      quantities: { ...this.state.quantities },
      step: this.currentStep() || null,
    };

    try {
      // 何も選んでいない状態は残す意味がないので消します。
      if (!data.category && !data.size && !data.variant) {
        window.localStorage.removeItem(this.storageKey());
        return;
      }
      window.localStorage.setItem(this.storageKey(), JSON.stringify(data));
    } catch (error) {
      /* 保存できない設定のブラウザでは、記憶なしで通常どおり動きます。 */
    }
  }

  clearState() {
    try {
      window.localStorage.removeItem(this.storageKey());
    } catch (error) {
      /* 消せなくても支障はありません。 */
    }
  }

  restoreState() {
    let data = null;
    try {
      const raw = window.localStorage.getItem(this.storageKey());
      if (!raw) return false;
      data = JSON.parse(raw);
    } catch (error) {
      return false;
    }

    if (!data || data.v !== ProductBuilder.STORAGE_VERSION) return false;

    // 時間が経ちすぎた内容は、在庫や価格が変わっている可能性があるため使いません。
    if (!data.t || Date.now() - data.t > ProductBuilder.STORAGE_MAX_AGE) {
      this.clearState();
      return false;
    }

    this.restoring = true;
    try {
      const category = data.category ? this.card('category', data.category) : null;
      if (category) this.selectCategory(category);

      const size = data.size ? this.card('size', data.size, { enabledOnly: true }) : null;
      if (size) this.selectSize(size);

      // 保存されている色と数量を、いまの在庫の範囲で戻します。
      if (data.quantities && typeof data.quantities === 'object') {
        Object.entries(data.quantities).forEach(([id, qty]) => {
          const card = this.root.querySelector(
            `[data-item][data-value="${CSS.escape(id)}"]:not([data-soldout])`
          );
          if (!card || !Number.isFinite(qty)) return;
          const capped = Math.min(Math.max(qty, 0), this.maxOf(card));
          if (capped > 0) this.state.quantities[id] = capped;
        });
      }

    } finally {
      this.restoring = false;
    }

    // 何も復元できなかった場合は、通常どおり最初から始めます。
    if (!this.state.category && !this.state.size && this.totalQuantity() === 0) return false;

    this.refresh();
    const stepIndex = this.steps.indexOf(data.step);
    this.goTo(stepIndex >= 0 ? stepIndex : this.firstUnansweredIndex(), { animate: false });
    return true;
  }

  /* ---------- 選択処理 ---------- */

  /*
   * 縦積みで、いちばん下に見せる段の位置です。
   * firstUnansweredIndex は「全部答え終わると 0」を返す作りなので、
   * 段の開示にはそのまま使えません。ここでは最後の段まで開きます。
   */
  stackLimit() {
    const first = this.steps.findIndex((step) => {
      if (step === 'category') return !this.state.category;
      if (step === 'size') return !this.state.size;
      if (step === 'color') return this.totalQuantity() === 0;
      return true;
    });
    return first === -1 ? this.steps.length - 1 : first;
  }

  /* このカードが属する段の位置です。 */
  stepIndexOfCard(card) {
    const panel = card.closest('[data-panel]');
    return panel ? this.steps.indexOf(panel.dataset.panel) : -1;
  }

  /* その段を選び直すと、この先の選択が消えるかどうかです。 */
  losesLaterChoices(index) {
    if (index < 0) return false;
    const later = this.steps.slice(index + 1);
    if (later.includes('size') && this.state.size) return true;
    if (later.includes('color') && this.totalQuantity() > 0) return true;
    return false;
  }

  askConfirm(onOk) {
    if (!this.confirmEl) {
      onOk();
      return;
    }
    const text = this.confirmEl.querySelector('[data-confirm-text]');
    if (text) text.textContent = this.config.confirmChangeText || '';
    this.pendingConfirm = onOk;
    this.confirmEl.hidden = false;
  }

  closeConfirm() {
    this.pendingConfirm = null;
    if (this.confirmEl) this.confirmEl.hidden = true;
  }

  onChoice(card) {
    if (card.disabled) return;

    // 前の段を選び直すときだけ、この先の選択が消えることを伝えます。
    const index = this.stepIndexOfCard(card);
    const isChosen = card.getAttribute('aria-pressed') === 'true';
    const here = this.isStacked() ? this.stackLimit() : this.stepIndex;
    if (index >= 0 && index < here && !isChosen && this.losesLaterChoices(index)) {
      this.askConfirm(() => this.applyChoice(card));
      return;
    }
    this.applyChoice(card);
  }

  applyChoice(card) {
    // すでに選んでいるカードをもう一度押したときは、進む合図とみなします。
    // 色は個数を扱うため、対象から外します。
    const choice = card.dataset.choice;
    const advanceOnRepeat =
      card.getAttribute('aria-pressed') === 'true' && choice !== 'color';

    switch (choice) {
      case 'category':
        this.selectCategory(card);
        break;
      case 'size':
        this.selectSize(card);
        break;
      default:
        return;
    }

    this.refresh();
    if (advanceOnRepeat) this.moveNext();
  }

  selectCategory(card) {
    this.state.category = card;
    this.state.size = null;
    this.state.quantities = {};
    this.markGroup('category', card);
    // カテゴリーを選び直したときは、下位の選択状態も見た目ごとリセットします。
    this.markGroup('size', null);
    this.markGroup('color', null);

    const group = this.sizeGroup();
    const sizes = group ? this.enabledCards(group, 'size') : [];
    if (sizes.length === 1) this.selectSize(sizes[0]);
  }

  selectSize(card) {
    this.state.size = card;
    this.state.quantities = {};
    this.markGroup('size', card);
    this.markGroup('color', null);

  }

  /* 同じ役割のカードのうち、選ばれたものだけを選択状態にします。 */
  markGroup(choice, card) {
    this.root
      .querySelectorAll(`[data-choice="${choice}"]`)
      .forEach((el) => el.setAttribute('aria-pressed', String(el === card)));
  }

  enabledCards(scope, choice) {
    return Array.from(scope.querySelectorAll(`[data-choice="${choice}"]:not([disabled])`));
  }

  /* 選択カードを 種類 + 値 で引きます。 */
  card(choice, value, options = {}) {
    if (value == null) return null;
    const enabled = options.enabledOnly ? ':not([disabled])' : '';
    return this.root.querySelector(
      `[data-choice="${choice}"][data-value="${CSS.escape(value)}"]${enabled}`
    );
  }

  /* data-*-group で束ねた選択肢のまとまりを引きます。 */
  group(name, value) {
    if (value == null) return null;
    return this.root.querySelector(`[data-${name}-group="${CSS.escape(value)}"]`);
  }

  sizeGroup() {
    return this.state.category ? this.group('size', this.state.category.dataset.value) : null;
  }

  colorGroup() {
    return this.state.size ? this.group('color', this.state.size.dataset.value) : null;
  }

  /* ---------- 色ごとの数量 ---------- */

  /* 画面に並んでいる色カードのうち、いま表示中のものです。 */
  itemCards() {
    const group = this.colorGroup();
    return group ? Array.from(group.querySelectorAll('[data-item]')) : [];
  }

  quantityOf(card) {
    return this.state.quantities[card.dataset.value] || 0;
  }

  /* その色をいくつまで選べるか。在庫が分からないときは上限で縛りません。 */
  /*
   * 在庫が分かる商品は在庫数をそのまま上限にします。
   * 在庫を追跡していない、または在庫数が取れない場合だけ設定値を使います。
   */
  maxOf(card) {
    const fallback = Number(this.config.maxQuantity) || 10;
    const tracked =
      card.dataset.inventoryManagement === 'shopify' && card.dataset.inventoryPolicy !== 'continue';
    if (!tracked) return fallback;
    const stock = Number(card.dataset.inventoryQuantity) || 0;
    if (stock <= 0) return fallback;
    return stock;
  }

  setQuantity(card, value) {
    const next = Math.min(Math.max(value, 0), this.maxOf(card));
    if (next <= 0) delete this.state.quantities[card.dataset.value];
    else this.state.quantities[card.dataset.value] = next;
    this.refresh();
  }

  changeQuantity(card, diff) {
    this.setQuantity(card, this.quantityOf(card) + diff);
  }

  /* 選ばれている合計個数です。1以上なければカートに進めません。 */
  totalQuantity() {
    return Object.values(this.state.quantities).reduce((sum, n) => sum + n, 0);
  }

  /* いま選ばれている色カードの一覧です（数量が1以上のもの）。 */
  chosenItems() {
    return this.itemCards().filter((card) => this.quantityOf(card) > 0);
  }

  /* 色カードの表示（数量・選択状態・上限）を更新します。 */
  refreshItems() {
    // 別のサイズのカードに古い数字が残らないよう、表示中でないものは 0 に戻します。
    const current = new Set(this.itemCards());
    this.root.querySelectorAll('[data-item]').forEach((card) => {
      if (current.has(card)) return;
      card.setAttribute('aria-pressed', 'false');
      const input = card.querySelector('[data-item-input]');
      if (input) input.value = '0';
    });

    this.itemCards().forEach((card) => {
      const qty = this.quantityOf(card);
      const max = this.maxOf(card);
      card.setAttribute('aria-pressed', String(qty > 0));

      const input = card.querySelector('[data-item-input]');
      if (input) {
        input.value = String(qty);
        input.max = String(max);
      }
      const minus = card.querySelector('[data-item-step="-1"]');
      const plus = card.querySelector('[data-item-step="1"]');
      if (minus) minus.disabled = qty <= 0;
      if (plus) plus.disabled = qty >= max;
    });
  }

  /* ---------- ステップ制御 ---------- */

  /* 選択内容に応じて、実際にたどるステップを組み立て直します。 */
  buildSteps() {
    // おすすめの詳細画面と完了画面は、通常のステップとは切り離して単独で表示します。
    if (this.showDetail) return ['detail'];
    if (this.completed) return ['done'];

    const steps = [];

    if (this.root.querySelectorAll('[data-choice="category"]').length > 1) steps.push('category');

    // 選択肢が1つしかなく、すでに自動で選ばれている段は省略します。
    // 逆に在庫切れなどで自動選択できなかった場合は、理由が見えるよう表示したままにします。
    if (this.needsSizeStep()) steps.push('size');

    // 色と個数は同じ画面です。色が1種類でも個数を決める必要があるため必ず通します。
    steps.push('color');

    return steps;
  }

  /*
   * サイズを選ぶ段を通すかどうかです。
   * 選択肢が1つしかなく、すでに自動で選ばれているなら省略します。
   * 在庫切れなどで自動選択できなかった場合は、理由が見えるよう残します。
   */
  needsSizeStep() {
    const group = this.sizeGroup();
    if (!group) return false;
    return group.querySelectorAll('[data-choice="size"]').length > 1 || !this.state.size;
  }

  currentStep() {
    return this.steps[this.stepIndex];
  }

  /* まだ答えていない最初の画面を返します。省略された画面があっても正しく飛べます。 */
  firstUnansweredIndex() {
    const index = this.steps.findIndex((step) => {
      if (step === 'category') return !this.state.category;
      if (step === 'size') return !this.state.size;
      if (step === 'color') return this.totalQuantity() === 0;
      // 個数から先は、答えの有無にかかわらず必ず通ります。
      return true;
    });
    return index === -1 ? 0 : index;
  }

  panelOf(name) {
    return this.panels.find((panel) => panel.dataset.panel === name) || null;
  }

  panelIndexOf(name) {
    return this.panels.findIndex((panel) => panel.dataset.panel === name);
  }

  canAdvance() {
    switch (this.currentStep()) {
      case 'category':
        return Boolean(this.state.category);
      case 'size':
        return Boolean(this.state.size);
      case 'color':
        return this.totalQuantity() >= 1;
      case 'detail':
      case 'done':
        return false;
      default:
        return false;
    }
  }

  moveNext() {
    if (this.stepIndex >= this.steps.length - 1 || !this.canAdvance()) {
      this.goTo(this.stepIndex, { animate: true });
      return;
    }
    this.goTo(this.stepIndex + 1);
  }

  movePrev() {
    // おすすめの詳細画面から戻るときは、完了画面へ返します。
    if (this.currentStep() === 'detail') {
      this.closeDetail();
      return;
    }
    if (this.stepIndex === 0) {
      this.goTo(this.stepIndex, { animate: true });
      return;
    }
    this.goTo(this.stepIndex - 1);
  }

  onNext() {
    const step = this.currentStep();
    if (step === 'detail') this.confirmDetail();
    else if (step === 'color' || this.isStacked()) this.addToCart();
    else this.moveNext();
  }

  /* 完了画面から「次の商品を選ぶ」で、最初の質問に戻ります。 */
  restart(options = {}) {
    const { keepSize = null } = options;

    this.completed = false;
    this.showDetail = false;
    this.state.quantities = {};
    this.showError('');

    // ギフトの選択は持ち越しません。前回のラッピングが残ったままだと、
    // 気づかないうちに次の商品にもラッピング代が加算されてしまうためです。

    if (keepSize) {
      // おすすめから来た場合は、その商品を選んだ状態で続きから始めます。
      this.selectSizeWithCategory(keepSize);
    } else {
      this.state.category = null;
      this.state.size = null;
      this.markGroup('category', null);
      this.markGroup('size', null);
      this.markGroup('color', null);
      this.autoSelectSingleCategory();
    }

    this.refresh();
    this.goTo(keepSize ? this.firstUnansweredIndex() : 0, { animate: false });
  }

  /* おすすめカードの押下です。いきなり選択には進まず、まず詳細画面を開きます。 */
  onRecommendation(card) {
    this.detailSource = card;
    this.completed = false;
    this.showDetail = true;
    this.refresh();
    this.goTo(0, { animate: true });
  }

  /* 詳細画面の「この商品を選ぶ」です。ここで初めて選択に進みます。 */
  confirmDetail() {
    const card = this.detailSource;
    if (!card) return;

    if (card.dataset.recoUrl) {
      this.leaveTo(card.dataset.recoUrl);
      return;
    }

    this.showDetail = false;

    if (card.dataset.recoSize) {
      const sizeCard = this.card('size', card.dataset.recoSize, { enabledOnly: true });
      if (sizeCard) this.restart({ keepSize: sizeCard });
      return;
    }

    if (card.dataset.recoCategory) {
      const categoryCard = this.card('category', card.dataset.recoCategory);
      if (!categoryCard) return;
      this.restart();
      this.selectCategory(categoryCard);
      this.refresh();
      this.goTo(this.firstUnansweredIndex(), { animate: false });
    }
  }

  /* 詳細画面から完了画面へ戻ります。 */
  closeDetail() {
    this.showDetail = false;
    this.completed = true;
    this.refresh();
    this.goTo(0, { animate: true });
  }

  /* 詳細画面の中身を、押されたおすすめカードから組み立てます。 */
  refreshDetail() {
    if (!this.showDetail || !this.detailSource) return;
    const card = this.detailSource;

    const media = this.root.querySelector('[data-detail-media]');
    if (media) {
      media.replaceChildren();
      const image = card.querySelector('.pb-reco__img');
      if (image) media.appendChild(image.cloneNode(true));
    }

    const fill = (selector, value) => {
      const el = this.root.querySelector(selector);
      if (!el) return;
      el.textContent = value || '';
      el.hidden = !value;
    };
    fill('[data-detail-title]', card.dataset.recoTitle);
    fill('[data-detail-note]', card.dataset.recoNote);
    // カテゴリーのおすすめは価格が定まらないため、金額は出しません。
    fill('[data-detail-price]', card.dataset.recoCategory ? '' : card.dataset.recoPrice);
  }

  goTo(index, options = {}) {
    const { animate = true } = options;
    const previousStep = this.currentStep();
    this.stepIndex = Math.max(0, Math.min(index, this.steps.length - 1));

    if (!animate) this.track.classList.add('is-dragging');
    // 縦に積むときは横へ動かしません。
    this.track.style.transform = this.isStacked()
      ? ''
      : `translateX(-${this.panelIndexOf(this.currentStep()) * 100}%)`;
    if (!animate) {
      // 次のフレームでアニメーションを戻し、初期表示が動かないようにします。
      window.requestAnimationFrame(() => this.track.classList.remove('is-dragging'));
    }

    this.refresh();

    if (this.currentStep() !== previousStep) {
      const panel = this.panelOf(this.currentStep());
      if (panel) panel.scrollTop = 0;
    }
  }

  syncHeight() {
    if (this.isModal) return;
    const panel = this.panelOf(this.currentStep());
    if (panel) this.stage.style.height = `${panel.offsetHeight}px`;
  }

  /* ---------- 再描画 ---------- */

  /* パソコンでは、答え終わった段と今の段だけを縦に並べます。 */
  isStacked() {
    return Boolean(this.wide && this.wide.matches);
  }

  refreshStack() {
    const stacked = this.isStacked();
    this.root.classList.toggle('pb-stacked', stacked);

    if (!stacked) {
      // スマホは横スライドのままなので、隠した段を戻します。
      this.panels.forEach((panel) => panel.classList.remove('is-hidden'));
      return;
    }

    const current = this.currentStep();
    const limit = this.stackLimit();

    // 新しい段が出たら、そこまで送ります。画面の外に出ると気づけないためです。
    if (this.lastStackLimit !== undefined && limit > this.lastStackLimit) {
      const next = this.panelOf(this.steps[limit]);
      if (next) {
        window.requestAnimationFrame(() =>
          next.scrollIntoView({ behavior: 'smooth', block: 'start' })
        );
      }
    }
    this.lastStackLimit = limit;

    this.panels.forEach((panel) => {
      const name = panel.dataset.panel;
      let show;
      if (current === 'done' || current === 'detail') {
        // 完了とおすすめの詳細は、これまでの段と入れ替えて出します。
        show = name === current;
      } else {
        // 縦積みでは「すすむ」を押さないので、答えた分だけ下へ伸ばします。
        const index = this.steps.indexOf(name);
        show = index >= 0 && index <= limit;
      }
      panel.classList.toggle('is-hidden', !show);
    });
  }

  refresh() {
    this.steps = this.buildSteps();
    if (this.stepIndex > this.steps.length - 1) this.stepIndex = this.steps.length - 1;

    // 段の見せ方を先に決めます。この結果を見て、触れる段を判定するためです。
    this.refreshStack();
    this.refreshVisibility();
    this.refreshShipping();
    this.refreshItems();
    this.refreshDone();
    this.refreshDetail();
    this.refreshRecap();
    this.refreshBar();
    this.syncHeight();
    this.saveState();
  }

  /* 完了画面の文言と、おすすめ商品の出し分けです。 */
  refreshDone() {
    if (!this.completed) return;

    if (this.doneItemEl) {
      // 「◯◯をカートに追加しました。」と、何を入れたのかを主語にします。
      this.doneItemEl.textContent = (this.config.addedText || '[商品]をカートに追加しました。').replace(
        '[商品]',
        this.itemName()
      );
    }

    if (this.cartCountEl) {
      this.cartCountEl.textContent = (this.config.cartCountText || '').replace(
        '[数量]',
        String(this.cartCount)
      );
    }

    // この商品用に手動指定したおすすめがあれば、それを優先します。
    const manual = this.state.size ? this.group('reco', this.state.size.dataset.value) : null;
    const fallback = this.root.querySelector('[data-reco-fallback]');

    this.root.querySelectorAll('[data-reco-group]').forEach((group) => {
      group.hidden = group !== manual;
    });

    if (fallback) {
      fallback.hidden = Boolean(manual);
      if (!manual) {
        // いま買ったカテゴリーを除き、最大3件までに絞ります。
        const currentCategory = this.state.category ? this.state.category.dataset.value : null;
        let shown = 0;
        fallback.querySelectorAll('[data-reco-category]').forEach((card) => {
          const skip = card.dataset.recoCategory === currentCategory || shown >= 3;
          card.hidden = skip;
          if (!skip) shown += 1;
        });
        fallback.hidden = shown === 0;
      }
    }

    const hasReco = Boolean(manual) || Boolean(fallback && !fallback.hidden);
    if (this.recoHeadingEl) this.recoHeadingEl.hidden = !hasReco;
    // おすすめが1件も無ければ、枠ごと出しません。
    if (this.recoSecEl) this.recoSecEl.hidden = !hasReco;
  }

  refreshVisibility() {
    // 選ばれたカテゴリー・サイズに対応するグループだけを表示します。
    const sizeGroupId = this.state.category ? this.state.category.dataset.value : null;
    this.root.querySelectorAll('[data-size-group]').forEach((group) => {
      group.hidden = group.dataset.sizeGroup !== sizeGroupId;
    });

    const colorGroupId = this.state.size ? this.state.size.dataset.value : null;
    this.root.querySelectorAll('[data-color-group]').forEach((group) => {
      group.hidden = group.dataset.colorGroup !== colorGroupId;
    });

    /*
     * 段の番号は、実際に通る段の並びから振り直します。
     * サイズが1種類の商品ではサイズ段が省略されるため、色が2番目になります。
     */
    this.panels.forEach((panel) => {
      const no = panel.querySelector('[data-step-no]');
      if (!no) return;
      const index = this.steps.indexOf(panel.dataset.panel);
      if (index >= 0) no.textContent = String(index + 1);
    });

    /*
     * 価格は本来サイズのカードに出しています。
     * サイズ段を省略する商品はそこを通らないため、色のカードに出します。
     */
    const colorPanel = this.panelOf('color');
    if (colorPanel) colorPanel.classList.toggle('is-priced', !this.needsSizeStep());

    /*
     * 表示していないパネルは、キーボード操作や読み上げの対象から外します。
     * 縦に積むときは見えている段すべてを触れるようにします
     * （見えているのに押せない、という状態を作らないためです）。
     */
    const active = this.currentStep();
    const stacked = this.isStacked();
    this.panels.forEach((panel) => {
      const usable = stacked
        ? !panel.classList.contains('is-hidden')
        : panel.dataset.panel === active;
      panel.inert = !usable;
      panel.setAttribute('aria-hidden', String(!usable));
    });
  }

  /* 選んだ商品の呼び名です。商品名にサイズが含まれるため、色だけを添えます。 */
  /* 完了画面などに出す、選んだ内容の呼び名です。色が複数なら並べて示します。 */
  itemName() {
    const product = this.state.size
      ? this.state.size.dataset.productTitle || this.state.size.dataset.label
      : '';
    const colors = this.chosenItems().map(
      (card) => `${card.dataset.label}×${this.quantityOf(card)}`
    );
    if (!colors.length) return product;
    return [product, colors.join('、')].filter(Boolean).join(' / ');
  }

  /* 上部の送料の帯です。固定の一言だけを出し、金額の増減は追いません。 */
  refreshShipping() {
    if (!this.shipLeadEl) return;
    const threshold = Number(this.config.freeShippingThreshold) || 0;
    const text =
      threshold > 0
        ? (this.config.freeShippingBanner || '').replace('[金額]', this.formatMoney(threshold))
        : '';
    this.shipLeadEl.textContent = text;

    if (!this.shipNotesEl) return;
    const note = this.config.shippingNote || '';
    this.shipNotesEl.textContent = note;
  }

  /*
   * 下のボタンの上に、いまの選択を短くまとめて出します。
   * 縦積みでは段が長くなり、上に戻らないと確かめられないためです。
   */
  /* 選んだカードの写真を1枚取り出します。 */
  static thumbOf(card) {
    const img = card ? card.querySelector('img') : null;
    return img ? img.currentSrc || img.src : '';
  }

  /* 要約に並べる札を1枚作ります。 */
  buildChip(card, label, count) {
    const chip = document.createElement('span');
    chip.className = 'pb-chip';

    const src = ProductBuilder.thumbOf(card);
    if (src) {
      const img = document.createElement('img');
      img.src = src;
      img.alt = '';
      img.loading = 'lazy';
      chip.appendChild(img);
    }

    const text = document.createElement('span');
    text.className = 'pb-chip__text';
    text.textContent = label;
    chip.appendChild(text);

    if (count) {
      const n = document.createElement('span');
      n.className = 'pb-chip__count';
      n.textContent = `×${count}`;
      chip.appendChild(n);
    }
    return chip;
  }

  refreshRecap() {
    if (!this.recapEl) return;
    if (!this.isStacked() || this.completed || this.showDetail) {
      this.recapEl.hidden = true;
      return;
    }

    this.recapEl.textContent = '';

    /*
     * 商品とサイズは「何を買うか」、色は「どれをいくつ」で役割が違います。
     * 同じ見た目で並べると読み分けられないので、
     * 前半は文字だけの見出し、後半だけ写真つきの札にします。
     */
    const lead = [];
    if (this.state.category) lead.push(this.state.category.dataset.label);
    if (this.state.size) lead.push(this.state.size.dataset.label);
    if (lead.length) {
      const head = document.createElement('span');
      head.className = 'pb-recap__lead';
      head.textContent = lead.join(' / ');
      this.recapEl.appendChild(head);
    }

    const chosen = this.chosenItems();

    // まだ何も選んでいないときは、ここに何が出るのかを薄く示します。
    if (lead.length === 0 && chosen.length === 0) {
      const hint = document.createElement('span');
      hint.className = 'pb-recap__hint';
      hint.textContent = this.config.recapEmptyText || '';
      this.recapEl.appendChild(hint);
      this.recapEl.hidden = false;
      return;
    }

    chosen.forEach((card) => {
      this.recapEl.appendChild(this.buildChip(card, card.dataset.label, this.quantityOf(card)));
    });

    const total = this.totalQuantity();
    if (total > 0) {
      const sum = document.createElement('span');
      sum.className = 'pb-recap__total';
      sum.textContent = `合計 ${total}点`;
      this.recapEl.appendChild(sum);
    }
    /*
     * 中身が無くても場所は残します。
     * 選ぶたびに箱の高さが動くと、ボタンの位置が定まらないためです。
     */
    this.recapEl.hidden = false;
  }

  refreshBar() {
    const step = this.currentStep();
    const isColor = step === 'color';
    const isDone = step === 'done';
    const isDetail = step === 'detail';

    // 1段目の主ボタンは画面ごとに役割が変わります。
    let mainText = null;
    if (isColor) mainText = this.config.addToCartText;
    else if (isDetail) {
      const toProductPage = Boolean(this.detailSource && this.detailSource.dataset.recoUrl);
      mainText = toProductPage ? this.config.viewProductText : this.config.chooseText;
    } else if (!isDone) mainText = this.config.nextText;

    if (this.nextBtn) {
      this.nextBtn.hidden = isDone;
      if (mainText) {
        this.nextBtn.textContent = mainText;
        this.nextBtn.disabled = isDetail ? false : !this.canAdvance();
      }
    }

    // 完了画面だけ、続けるかお会計かを選んでもらいます。
    if (this.continueBtn) this.continueBtn.hidden = !isDone;
    if (this.checkoutBtn) this.checkoutBtn.hidden = !isDone;

    // 縦に積むときは全部見えているので、前後の移動は要りません。
    if (this.isStacked() && !isDone && !isDetail) {
      if (this.prevBtn) this.prevBtn.hidden = true;
      if (this.nextBtn) {
        const canAdd = this.totalQuantity() > 0;
        this.nextBtn.hidden = false;
        this.nextBtn.textContent = this.config.addToCartText;
        this.nextBtn.disabled = !canAdd;
      }
      return;
    }

    // 「もどる」は位置を固定したいので、戻れない画面では押せなくするだけにします。
    if (this.prevBtn) {
      this.prevBtn.hidden = isDone;
      this.prevBtn.disabled = !isDetail && this.stepIndex === 0;
    }
  }

  /* 商品とラッピングの小計です（送料は含みません）。 */
  total() {
    return this.chosenItems().reduce(
      (sum, card) => sum + (Number(card.dataset.price) || 0) * this.quantityOf(card),
      0
    );
  }

  formatMoney(cents) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'ja-JP', {
        style: 'currency',
        currency: this.config.currency || 'JPY',
      }).format(cents / 100);
    } catch (error) {
      return `¥${Math.round(cents / 100).toLocaleString()}`;
    }
  }

  /* ---------- カート追加 ---------- */

  async addToCart() {
    if (this.totalQuantity() < 1 || this.nextBtn.disabled) return;

    this.setBusy(true);
    this.showError('');

    // Shopify Flow でタグ付けするための印です。
    // 「_」始まりのプロパティはカート・チェックアウト・注文確認のいずれにも表示されません。
    const marker = { [this.config.trackingKey]: this.config.trackingValue };

    // 選ばれた色をまとめて1回のリクエストで追加します。
    const items = this.chosenItems().map((card) => ({
      id: Number(card.dataset.value),
      quantity: this.quantityOf(card),
      properties: { ...marker },
    }));

    try {
      const response = await fetch(`${this.config.cartAddUrl}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.description || this.config.cartErrorText);
      }

      // 買い終えた内容を次回に持ち越さないよう、記録を消します。
      this.clearState();
      this.cartCount = await this.fetchCartCount();
      this.nextBtn.classList.remove('is-busy');
      this.pushDataLayer('pb_add_to_cart', {
        value: this.total() / 100,
        currency: this.config.currency || 'JPY',
        quantity: this.state.quantity,
        item_name: this.itemName(),
      });

      // カートへは遷移せず、そのまま完了画面へ進みます。
      this.completed = true;
      this.refresh();
      this.goTo(0, { animate: true });
      this.updateCartBubble();
    } catch (error) {
      this.showError(error.message || this.config.cartErrorText);
      this.setBusy(false);
    }
  }

  async fetchCartCount() {
    try {
      const response = await fetch(`${this.config.cartUrl}.js`, {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return this.cartCount + this.state.quantity;
      const cart = await response.json();
      // 送料無料の判定に使うため、金額も控えておきます。
      this.cartTotal = cart.total_price || 0;
      return cart.item_count;
    } catch (error) {
      return this.cartCount + this.state.quantity;
    }
  }

  /* ヘッダーのカート個数バッジがあれば、数字を合わせておきます。 */
  updateCartBubble() {
    document
      .querySelectorAll('.cart-count-bubble span[aria-hidden="true"]')
      .forEach((el) => {
        el.textContent = String(this.cartCount);
      });
  }

  setBusy(busy) {
    this.nextBtn.disabled = busy;
    this.nextBtn.classList.toggle('is-busy', busy);
    this.nextBtn.textContent = busy ? '処理中…' : this.config.addToCartText;
  }

  showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message;
    this.errorEl.hidden = !message;
    this.syncHeight();
  }

  /* GTM（dataLayer）へイベントを送ります。未導入の環境でも安全に無視されます。 */
  pushDataLayer(event, payload = {}) {
    if (!Array.isArray(window.dataLayer)) return;
    window.dataLayer.push({ event, ...payload });
  }

}

function initProductBuilders(scope = document) {
  scope.querySelectorAll('[data-product-builder]').forEach((root) => {
    if (root.dataset.pbReady === 'true') return;
    root.dataset.pbReady = 'true';
    new ProductBuilder(root);
  });
}

initProductBuilders();

// テーマエディターでセクションが再読み込みされたときにも動くようにします。
document.addEventListener('shopify:section:load', (event) => initProductBuilders(event.target));
